import { MesfModal } from "@dexteel/mesf-core";
import { Grid, TextField } from "@mui/material";
import {
  AgGridEvent,
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get, isNil } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { getCatalogElements } from "../../../../laboratory/repositories/CatalogElementRepository";
import { GradeChemistryLimit } from "../../../models/CatalogGrade";
import {
  deleteGradeChemistryLimit,
  upsertCatGradeChemistryLimit,
} from "../../../repositories/CatalogGradeRepository";

type Props = {
  gradeId: number;
  onSelectionChanged: Function;
  elementsSelected: GradeChemistryLimit[];
  elementsOfGrade: GradeChemistryLimit[];
  show: boolean;
  onHide: (newElements: boolean) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
};

export const AddElementModal = ({
  gradeId,
  onSelectionChanged,
  elementsSelected,
  elementsOfGrade,
  show,
  onHide,
  setError,
  setNotificationMessage,
}: Props) => {
  const [filterInput, setFilterInput] = useState("");
  const [allElements, setAllElements] = useState<GradeChemistryLimit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { handleSubmit } = useForm({
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<any> = async (data) => {
    setIsLoading(true);
    if (gridApi) {
      const newElements = elementsSelected.filter((selectedRow) => {
        const isPresent = elementsOfGrade.some(
          (element) => element.ElementId === selectedRow.ElementId,
        );
        return !isPresent;
      });
      const oldElements = elementsOfGrade.filter((selectedRow) => {
        const isPresent = elementsSelected.some(
          (element) => element.ElementId === selectedRow.ElementId,
        );
        return !isPresent;
      });
      newElements.map(async (e) => {
        const resp = await upsertCatGradeChemistryLimit({
          gradeId: gradeId,
          elementId: e.ElementId!,
        });
        if (!resp.ok) {
          onHide(false);
          setError(resp.message);
        } else {
          setNotificationMessage("The elements were modified succesfully.");
          onHide(true);
        }
      });
      oldElements.map(async (e) => {
        const resp = await deleteGradeChemistryLimit(gradeId, e.ElementId!);
        if (!resp.ok) {
          onHide(false);
          setError(resp.message);
        } else {
          setNotificationMessage("The elements were modified succesfully.");
          onHide(true);
        }
      });
    }

    setIsLoading(false);
  };

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    const resp = await getCatalogElements();
    if (resp.ok) {
      const rows = get(
        resp,
        "data.tables[0].rows",
        [],
      ) as GradeChemistryLimit[];
      setAllElements(rows);
    } else {
      setError(resp.message);
    }
    setIsLoading(false);
  };

  const handlerFilter = (e: any) => {
    setFilterInput(e.target.value);
  };

  const sortRows = (event: AgGridEvent) => {
    event.api.applyColumnState({
      state: [{ colId: "ElementName", sort: "asc", sortIndex: 0 }],
    });
  };

  const rows = useMemo(() => {
    return allElements.map(({ ElementId, ElementName }) => {
      return {
        ElementId: ElementId,
        ElementName: ElementName,
      };
    });
  }, [allElements]);

  const columns: ColDef[] = [
    {
      field: "",
      flex: 1,
      maxWidth: 100,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      sortIndex: 1,
      sortable: false,
    },
    {
      field: "ElementName",
      headerName: "Element",
      flex: 2,
      sortIndex: 0,
      comparator: (a: any, b: any, nodeA, nodeB) => {
        const aSelected = nodeA.isSelected();
        const bSelected = nodeB.isSelected();

        if (aSelected && !bSelected) {
          return -1;
        } else if (!aSelected && bSelected) {
          return 1;
        }

        return nodeA.data.ElementName?.localeCompare(nodeB.data.ElementName);
      },
    },
  ];

  const defaultColDef = useMemo(
    () => ({
      resizable: false,
      filter: false,
      sortable: true,
    }),
    [],
  );

  const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
    sortRows(event);
    const selectedRows = event.api.getSelectedRows();
    onSelectionChanged(selectedRows);
    event.api.onSortChanged();
  };

  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent) => {
      params.api.forEachNode((node) => {
        node.setSelected(
          !!node.data &&
            !isNil(
              elementsOfGrade.find((p) => p.ElementId === node.data.ElementId),
            ),
        );
      });
      sortRows(params);
    },
    [sortRows],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
    },
    [setGridApi],
  );

  useEffect(() => {
    gridApi?.setGridOption("quickFilterText", filterInput.trim());
  }, [filterInput, gridApi]);

  useEffect(() => {
    loadData().then(() => {});
  }, []);

  return (
    <>
      <MesfModal
        title={`COPY CHEMISTRIES LIMITS`}
        open={show}
        handleClose={() => onHide(false)}
        maxWidth="sm"
        id="ladle-modal"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "20px 30px" }}>
            <Grid
              container
              md={12}
              xs={12}
              style={{ padding: "0 0 10px", justifyContent: "space-between" }}
            >
              <Grid item md={12} xs={12}>
                <TextField
                  id="outlined-basic"
                  label="Search"
                  variant="outlined"
                  value={filterInput}
                  onChange={handlerFilter}
                  margin="dense"
                  style={{ width: "100%" }}
                />
              </Grid>
            </Grid>
            <div>
              <div style={{ width: "100%", height: "40vh" }}>
                <AgGridReact
                  getRowId={(params) => params.data.ElementId}
                  onGridReady={onGridReady}
                  rowHeight={25}
                  rowData={rows}
                  columnDefs={columns}
                  defaultColDef={defaultColDef}
                  onFirstDataRendered={onFirstDataRendered}
                  rowSelection={"multiple"}
                  animateRows
                  rowMultiSelectWithClick
                  onSelectionChanged={onAgGridSelectionChanged}
                />
              </div>
            </div>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 20px" }}>
            <FooterModalButtons
              isSubmitLoading={false}
              onHide={() => onHide(false)}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
    </>
  );
};
